import { useTheme } from 'styled-components';
import { useEffect, useMemo, useState } from 'react';
import Joyride, { ACTIONS, EVENTS } from 'react-joyride';
import { useLocation, useNavigate } from 'react-router-dom';
import { useContactPreferencesUpdate } from 'shared/hooks/api/contacts';
import { useEntityDisplayName } from 'shared/hooks/misc/fields';
import useDisclosure from 'shared/hooks/misc/useDisclosure';
import { EntityType } from 'shared/constants/entityTypes';
import useTimeout from 'shared/hooks/misc/useTimeout';
import OnboardingTooltip from './Tooltip';
import { sleep } from 'shared/utils/functions';
import { noop } from 'shared/utils/misc';
import { useAppStore } from 'App/store';
import {
  PRODUCT_TOURS,
  PORTAL_ADMIN_ONBOARDING_TARGET_CLASSES,
  NUM_PORTAL_ADMIN_ONBOARDING_TARGETS,
} from 'shared/constants/misc';

// Adds a bit of delay after the homepage mounts,
// so the tour start is less jarring.
const TOUR_DELAY_MS = 3000;
const tourName = PRODUCT_TOURS.PORTAL_ADMIN_ONBOARDING;
const getTargetClass = (num = 1) => {
  if (!num) throw new Error('Target class index cannot be zero-based');

  if (num > NUM_PORTAL_ADMIN_ONBOARDING_TARGETS)
    throw new Error('Index out of range');

  return PORTAL_ADMIN_ONBOARDING_TARGET_CLASSES[`TARGET_${num}`];
};

export default function OnboardingTour() {
  const {
    appState: { customerId, portalAdmin, user },
  } = useAppStore();
  const theme = useTheme();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [stepIndex, setStepIndex] = useState(0);
  const [updatePreferences] = useContactPreferencesUpdate();
  const [contactSingular, contactPlural] = useEntityDisplayName(
    EntityType.CONTACT
  );
  const [customerSingular] = useEntityDisplayName(EntityType.CUSTOMER);
  const [, delayElapsed] = useTimeout(noop, portalAdmin ? TOUR_DELAY_MS : null);
  const [shouldStart, { onOpen: startTour, onClose: stopTour }] =
    useDisclosure(false);

  const viewedTours = user?.preferences?.viewed_tours || [];
  const wasViewed = viewedTours.includes(tourName);

  const steps = useMemo(
    () => [
      {
        target: getTargetClass(1),
        content:
          'Welcome to the QBench Customer Portal! Find out more about how to get started using your new Portal admin account.',
        data: {
          next: async (data) => {
            const { step } = data;
            await sleep(1);
            document.querySelector(step.target).click();
            await sleep(200);
            goToNextStep();
          },
        },
        disableBeacon: true,
      },
      {
        target: getTargetClass(2),
        content: `As an admin, you can update your ${customerSingular} and ${contactSingular} information anytime.`,
        data: {
          next: async () => {
            navigate(`/portal/customer`);
            await sleep(2800);
            goToNextStep();
          },
        },
        disableBeacon: true,
        placement: 'left-start',
        styles: {
          options: { zIndex: 9999 },
        },
      },
      {
        target: getTargetClass(3),
        content: `Each ${customerSingular} has ${contactPlural}, and every ${contactSingular} is linked to a ${customerSingular}.`,
        data: {
          next: () => {
            goToNextStep();
          },
        },
        disableBeacon: true,
      },
      {
        target: getTargetClass(4),
        content: `When you add, remove, or edit Portal Users, the changes will also reflect on the lab's side.`,
        data: {
          next: () => {
            navigate('/');
            goToNextStep();
          },
        },
        disableBeacon: true,
      },
    ],
    [navigate, customerId, contactSingular, customerSingular]
  );

  useEffect(() => {
    if (delayElapsed && pathname === '/portal') startTour();
  }, [delayElapsed]);

  const goToNextStep = () => setStepIndex((prev) => prev + 1);

  function dismissTour() {
    if (wasViewed) return;
    stopTour();
    navigate('/');
    updatePreferences({
      viewed_tours: [...viewedTours, tourName],
    });
  }

  function handleJoyrideCallback(data) {
    const { type, step, action, size: totalSteps, index: stepNumber } = data;
    const hasNextStep = stepNumber < totalSteps;
    const isGoingNext = action === ACTIONS.NEXT;

    if (type === EVENTS.TOUR_END) {
      dismissTour();
    }

    if (type === EVENTS.STEP_AFTER) {
      if (isGoingNext && hasNextStep) {
        step.data.next?.(data);
      }
    }
  }

  return (
    <Joyride
      continuous
      showSkipButton
      hideBackButton
      hideCloseButton
      disableCloseOnEsc
      disableOverlayClose
      steps={steps}
      stepIndex={stepIndex}
      spotlightClicks={false}
      callback={handleJoyrideCallback}
      tooltipComponent={OnboardingTooltip}
      locale={{ skip: 'Dismiss', last: 'Dismiss' }}
      run={shouldStart && !wasViewed}
      styles={{
        options: {
          arrowColor: theme.color.palette.grey[100],
        },
      }}
    />
  );
}
